import React, { useEffect, useCallback, useState, useRef } from 'react';
import Slider from 'react-slick';
import { useStore } from '../../context/Context';
import Image from '../fragments/Image';
import Footer from '../global/Footer';

const Project = () => {
    const { posts, appError } = useStore();

    const projectHeaderRef = useRef(null);
    const sliderRef = useRef(null);
    const [infoOpen, setInfoOpen] = useState(false);
    const [isDesktopSlider, setIsDesktopSlider] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const cinemagraphRefs = useRef([]);

    const handleResize = useCallback(() => {
        if (projectHeaderRef.current) {
            const height = projectHeaderRef.current.clientHeight;
            document.documentElement.style.setProperty('--project-header-height', `${height}px`);
        }

        if (typeof window !== 'undefined') {
            setIsDesktopSlider(window.innerWidth > 960);
        }
    }, []);

    const toggleInfo = useCallback(() => {
        if (infoOpen === false) {
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
        } else {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        }
		setInfoOpen(prevState => !prevState);
        setTimeout(() => {
            handleResize(); 
    	}, 0);
    }, [handleResize, infoOpen]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleResize();
        }, 0);

        window.addEventListener('resize', handleResize);
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        if (posts && posts.length > 0) {
            const timeoutId = setTimeout(() => {
                handleResize();
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [posts, infoOpen, handleResize]);

    useEffect(() => {
        const post = posts[0];
        if (post?.slides?.length) {
            const hash = window.location.hash; // e.g. "#slide-2"
            if (hash.startsWith('#slide-')) {
                const index = parseInt(hash.replace('#slide-', ''), 10) - 1;
                setTimeout(() => {
                    if (isDesktopSlider && sliderRef.current) {
                        sliderRef.current.slickGoTo(index, true);
                    }
                }, 100);
            }
        }
    }, [posts, isDesktopSlider]);

    useEffect(() => {
        if (!isDesktopSlider) return;

        cinemagraphRefs.current.forEach((video, index) => {
            if (!video) return;

            if (index === activeSlide) {
                video.currentTime = 0;
                video.play().catch(() => {});
            } else {
                video.pause();
            }
        });
    }, [activeSlide, isDesktopSlider]);

    useEffect(() => {
        if (isDesktopSlider) return;
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const video = entry.target;
                    if (entry.isIntersecting) {
                        video.play().catch(() => {});
                    } else {
                        video.pause();
                    }
                });
            },
            { threshold: 0.5 }
        );

        cinemagraphRefs.current.forEach((video) => {
            if (video) observer.observe(video);
        });

        return () => observer.disconnect();
    }, [isDesktopSlider, posts]);

    const renderSlide = (slide, index) => {
        if (slide.type === 'image') {
            return (
                <div 
                    className={`c-project__slide c-project__slide--index-${index} c-project__slide--inset ${slide.width < slide.height ? 'c-project__slide--portrait' : ''}`}
                    key={index}
                >
                    <div className="c-project__slide__inner">
                        <div className="c-project__slide__media-wrapper" style={{ aspectRatio: slide.width && slide.height ? `${slide.width} / ${slide.height}` : 'auto' }}>
                            {slide.cinemagraph ? (
                                <video
                                    ref={(el) => cinemagraphRefs.current[index] = el}
                                    className="c-project__slide__cinemagraph"
                                    src={slide.cinemagraph}
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                />
                            ) : null}
                            <Image src={slide.src} alt={slide.alt} />
                        </div>
                    </div>
                </div>
            );
        } else if (slide.type === 'two_columns') {
            return (
                <div 
                    className={`c-project__slide c-project__slide--index-${index} c-project__slide--two-columns`}
                    key={index}
                >
                    <div className="c-project__slide__inner c-project__slide__inner--left">
                        <Image src={slide.left_src} width={slide.left_width} height={slide.left_height} alt={slide.left_alt} />
                    </div>
                    <div className="c-project__slide__inner c-project__slide__inner--right">
                        <Image src={slide.right_src} width={slide.right_width} height={slide.right_height} alt={slide.right_alt} />
                    </div>
                </div>
            );
        }

        return null;
    };

    if (appError) return null;
    if (!posts || posts.length === 0) return null;
    
    const post = posts[0];
    
    if (!post?.slides) return null;

    console.log('Project data:', post);

    return (
        <div className="c-page__block">
            <div className={`c-project ${infoOpen ? 'state-info-open' : ''}`}>
                <h2 className="c-project__title c-paragraph" ref={projectHeaderRef} dangerouslySetInnerHTML={{ __html: post.title }}></h2>
                <span 
                    className="c-project__overview-toggle c-paragraph c-paragraph--faded c-underline-on-hover" 
                    dangerouslySetInnerHTML={{ __html: infoOpen ? "Close" : "Overview" }} 
                    onClick={toggleInfo}
                ></span>
                <div className="c-project__overview">
                    <div className="c-project__overview__inner">
                        {post.title && (
                            <h2 className="c-project__overview__title c-paragraph c-paragraph--space" dangerouslySetInnerHTML={{ __html: post.title }}></h2>
                        )}
                        {post.project_type && post.location && (
                            <>
                                <h3 className="c-project__overview__type c-paragraph" dangerouslySetInnerHTML={{ __html: `Type: ${post.project_type}` }}></h3>
                                <h4 className="c-project__overview__location c-paragraph c-paragraph--space" dangerouslySetInnerHTML={{ __html: `Location: ${post.location}` }}></h4>
                            </>
                        )}
                        {post.project_type && !post.location && (
                            <>
                                <h3 className="c-project__overview__type c-paragraph" dangerouslySetInnerHTML={{ __html: `Type: ${post.project_type}` }}></h3>
                            </>
                        )}
                        {!post.project_type && post.location && (
                            <>
                                <h4 className="c-project__overview__location c-paragraph c-paragraph--space" dangerouslySetInnerHTML={{ __html: `Location: ${post.location}` }}></h4>
                            </>
                        )}

                        <div className="scope-text" dangerouslySetInnerHTML={{ __html: post.information }} />
                        {post.credits && post.credits.length > 0 && (
                            <>
                                <h3 className="c-project__overview__heading c-paragraph c-paragraph--faded">Partners and Collaborators</h3>
                                <ul className="c-project__overview__credits c-paragraph">
                                    {post.credits.map((creditItem, index) => (
                                        <React.Fragment key={index}>
                                            {creditItem.url ? (
                                                <li className="c-project__overview__credits__item">
                                                    <a 
                                                        className="c-project__overview__credits__item__inner c-project__overview__credits__item__inner--link c-paragraph" 
                                                        href={creditItem.url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        dangerouslySetInnerHTML={{ __html: '<span>' + creditItem.title + '</span>' }}
                                                    ></a>
                                                </li>
                                            ) : (
                                                <li className="c-project__overview__credits__item">
                                                    <span 
                                                        className="c-project__overview__credits__item__inner c-paragraph" 
                                                        dangerouslySetInnerHTML={{ __html: '<span>' + creditItem.title + '</span>' }}
                                                    ></span>
                                                </li>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </div>
                
                {post.slides && post.slides.length > 0 && (
                    <>
                        {isDesktopSlider ? (
                            <div className="c-project__slider">
                                <Slider
                                    ref={sliderRef}
                                    arrows={true}
                                    dots={false}
                                    infinite={true}
                                    slidesToShow={1}
                                    slidesToScroll={1}
                                    adaptiveHeight={true}
                                    afterChange={setActiveSlide}
                                >
                                    {post.slides.map((slide, index) => renderSlide(slide, index))}
                                </Slider>
                            </div>
                        ) : (
                            <>
                                {post.slides.map((slide, index) => renderSlide(slide, index))}
                            </>
                        )}
                    </>
                )}
                
            </div>

        </div>
    );
};

export default Project;